import {
  applyDispatchedVisibilities,
  type ApplyDispatchedVisibilitiesDeps,
} from '@/services/action/applyDispatchedVisibilities'
import { normalizeExchangeStates } from '@/common/utils/normalizeExchangeStates'
import { coerceStateBoolean } from '@/common/utils/resolveActionPageState'

function unwrapScriptResponseRoot(response: unknown): Record<string, unknown> | null {
  if (response == null || typeof response !== 'object') return null

  const root = response as Record<string, unknown>
  const nested = root.data
  if (
    nested != null &&
    typeof nested === 'object' &&
    !Array.isArray(nested) &&
    ('properties' in nested ||
      'jsResult' in nested ||
      'states' in nested ||
      'dispatched_visibilities' in nested ||
      'dispatchedVisibilities' in nested ||
      'local_result' in nested ||
      'client_script' in nested ||
      'form_id' in nested)
  ) {
    return nested as Record<string, unknown>
  }
  return root
}

/**
 * Достаёт dispatched_visibilities из ответа executeScript (middle onInlineMenuShow).
 * Поддерживает properties / jsResult / top-level и snake/camel case.
 */
export function extractDispatchedVisibilitiesFromScriptResponse(response: unknown): unknown {
  const data = unwrapScriptResponseRoot(response)
  if (!data) return []

  const fromProps = (props: unknown): unknown => {
    if (!props || typeof props !== 'object') return undefined
    const p = props as Record<string, unknown>
    return p.dispatched_visibilities ?? p.dispatchedVisibilities
  }

  const fromProperties = fromProps(data.properties)
  if (fromProperties != null) return fromProperties

  const localResult = data.local_result
  if (localResult && typeof localResult === 'object') {
    const lr = localResult as Record<string, unknown>
    const fromLocal = fromProps(lr.properties) ?? lr.dispatchedVisibilities ?? lr.dispatched_visibilities
    if (fromLocal != null) return fromLocal
  }

  if (data.dispatched_visibilities != null) return data.dispatched_visibilities
  if (data.dispatchedVisibilities != null) return data.dispatchedVisibilities

  const jsResult = data.jsResult
  if (jsResult && typeof jsResult === 'object') {
    const jr = jsResult as Record<string, unknown>
    const fromJr = fromProps(jr.properties) ?? jr.dispatched_visibilities ?? jr.dispatchedVisibilities
    if (fromJr != null) return fromJr
  }

  return []
}

/**
 * states с is_visible → setVisibleByLevel (ACTION id → deferred для inline-меню).
 * Middle onInlineMenuShow часто отдаёт visibility в states, а не в dispatched_visibilities.
 */
export function applyInlineMenuShowVisibilityStates(
  response: unknown,
  formProvider: ApplyDispatchedVisibilitiesDeps['formProvider'] | null | undefined,
): void {
  if (!formProvider?.setVisibleByLevel) return

  const data = unwrapScriptResponseRoot(response)
  const states = normalizeExchangeStates(data ?? response)
  for (const raw of states) {
    if (!raw || typeof raw !== 'object') continue
    const state = raw as Record<string, unknown>
    if (state.is_visible === undefined) continue

    const visible = coerceStateBoolean(state.is_visible)
    if (visible === undefined) continue

    const id = state.element_id
    if (id == null || String(id).trim() === '') continue

    const level = String(state.element_level ?? 'ACTION').trim() || 'ACTION'
    formProvider.setVisibleByLevel(level, id as string | number, visible)
  }
}

/**
 * Применяет visibility из ответа middle onInlineMenuShow.
 * setVisibleByLevel(action, id, …) → rememberDeferredActionVisibility → фильтр inline-меню.
 */
export function applyInlineMenuShowScriptVisibilities(
  response: unknown,
  formProvider: ApplyDispatchedVisibilitiesDeps['formProvider'] | null | undefined,
): void {
  if (!formProvider) return
  const raw = extractDispatchedVisibilitiesFromScriptResponse(response)
  applyDispatchedVisibilities(raw, {formProvider})
  applyInlineMenuShowVisibilityStates(response, formProvider)
}
